import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config';
import '../styles/procurement-admin.css';

const forms = {
  crops: [['name', 'Crop name']],
  units: [['code','Unit code'],['dimension','Dimension','dimensions'],['decimal_places','Decimal places (0–6)']],
  varieties: [['crop_id','Crop','crops'],['name','Variety name']],
  grades: [['crop_id','Crop','crops'],['name','Grade name']],
  packaging: [['name','Material name'],['unit_id','Stocking unit','units'],['nominal_tare_kg','Nominal tare kg']],
  mappings: [['crop_id','Crop','crops'],['variety_id','Variety','varieties']],
  schedules: [['crop_id','Crop','crops'],['variety_id','Variety','varieties'],['name','Schedule name']],
  dhalta: [['crop_id','Crop','crops'],['variety_id','Variety','varieties'],['name','Rule/version description'],['suggested_kg','Suggested deduction kg']],
  versions: [['schedule_id','Schedule','schedules'],['valid_from','Valid from'],['valid_to','Valid until']],
};
const central = ['crops','units','packaging'];
const labels = {crops:'Crops',units:'Procurement Units',packaging:'Packaging Materials',procurements:'Procurement Review',varieties:'Varieties',grades:'Grades',mappings:'Crop-to-Variety Mapping',schedules:'Rate Schedules',dhalta:'Dhalta Rules',versions:'Rate Versions / Grade Rates'};
function newId() {
  const bytes=crypto.getRandomValues(new Uint8Array(16));bytes[6]=(bytes[6]&15)|64;bytes[8]=(bytes[8]&63)|128;
  const h=Array.from(bytes,b=>b.toString(16).padStart(2,'0')).join('');
  return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
}

function localDateTimeToISO(value) {
  if (typeof value !== 'string' || !value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function formatDateTime(value, timeZone) {
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

function rateVersionValidation(values, schedules) {
  if (!values.schedule_id || !schedules.some(schedule => schedule.id === values.schedule_id)) return 'Select a schedule.';
  if (!values.valid_from) return 'Enter a valid start date and time.';
  const from = localDateTimeToISO(values.valid_from);
  if (!from) return 'Enter a valid start date and time.';
  if (!values.valid_to) return 'Enter a valid end date and time.';
  const to = localDateTimeToISO(values.valid_to);
  if (!to) return 'Enter a valid end date and time.';
  if (Date.parse(to) <= Date.parse(from)) return 'Valid until must be later than Valid from.';
  return '';
}

function dateTimePreview(value) {
  const iso = localDateTimeToISO(value);
  if (!iso) return null;
  return { local: formatDateTime(iso), utc: formatDateTime(iso, 'UTC') };
}

// Explicit seller sources only: a missing Farmer reference is never Walk-in.
function walkInDisplay(procurement) {
  if(procurement.field_snapshot?.seller?.kind==='WALK_IN') return procurement.field_snapshot.seller;
  if(procurement.seller_kind==='WALK_IN') return {
    name:procurement.walk_in_name,phone:procurement.walk_in_phone,location:procurement.walk_in_location,
  };
  return null;
}

export default function Procurement() {
  const [data,setData]=useState(null), [section,setSection]=useState(localStorage.getItem('role')==='super_admin'?'crops':'procurements');
  const [values,setValues]=useState({}),[message,setMessage]=useState(''),[busy,setBusy]=useState(false),[validationAttempted,setValidationAttempted]=useState(false);
  const [fpo]=useState(localStorage.getItem('fpo_id')||'');
  const pending=useRef(null), loadGeneration=useRef(0), submitting=useRef(false);
  const request=useCallback(async(path,body)=>{
    const headers={Authorization:localStorage.getItem('token'),'Content-Type':'application/json'};
    if(fpo) headers['X-FPO-ID']=fpo;
    const response=await fetch(`${API_BASE}/procurement/v1${path}`,{method:body?'POST':'GET',headers,...(body?{body:JSON.stringify(body)}:{})});
    const result=await response.json();if(!response.ok) throw new Error(result.message||'Procurement request failed');return result;
  },[fpo]);
  const load=useCallback(async()=>{
    const generation=++loadGeneration.current;
    try{const result=await request('/bootstrap');if(generation===loadGeneration.current){setData(result);setMessage('');}}
    catch(e){if(generation===loadGeneration.current){setData(null);setMessage(e.message);}}
  },[request]);
  useEffect(()=>{load();},[load]);
  const change=(key,value)=>setValues(v=>({...v,[key]:value,...(key==='crop_id'?{variety_id:''}:{})}));
  const rateValidation=section==='versions'?rateVersionValidation(values,data?.schedules||[]):'';
  async function submit(e){
    e.preventDefault();
    if(submitting.current)return;
    if(section==='versions'){
      setValidationAttempted(true);
      if(rateVersionValidation(values,data?.schedules||[]))return;
    }
    submitting.current=true;setBusy(true);setMessage('');
    try{
      let action=`master.${section}`,payload={...values};
      if(section==='versions'){
        action='rate.publish';const schedule=data.schedules.find(s=>s.id===values.schedule_id);
        payload={schedule_id:values.schedule_id,valid_from:localDateTimeToISO(values.valid_from),valid_to:localDateTimeToISO(values.valid_to),
          entries:data.grades.filter(g=>g.crop_id===schedule?.crop_id).map(g=>({grade_id:g.id,rate:values[`rate_${g.id}`]}))};
      }
      const fingerprint=JSON.stringify({action,payload,fpo});
      if(!pending.current||pending.current.fingerprint!==fingerprint) pending.current={fingerprint,command:{command_id:newId(),entity_id:newId(),action,data:payload}};
      await request('/commands',pending.current.command);pending.current=null;setValues({});setValidationAttempted(false);await load();setMessage('Saved. Published rules and versions are immutable.');
    }catch(e){setMessage(e.message);}finally{submitting.current=false;setBusy(false);}
  }
  async function enrol(){setBusy(true);try{await request('/enrol-self',{});await load();}catch(e){setMessage(e.message);}finally{setBusy(false);}}
  const superAdmin=data?.context.superAdmin??localStorage.getItem('role')==='super_admin';
  const fields=forms[section]||[];
  const rows=data?.[section]||[];
  const name=(source,id)=>{const row=data?.[source]?.find(row=>String(row.id)===String(id));return row?.name||row?.code||id||'—';};
  const cell=(key,row)=>{
    const sources={crop_id:'crops',variety_id:'varieties',grade_id:'grades',schedule_id:'schedules',unit_id:'units'};
    return sources[key]?name(sources[key],row[key]):String(row[key]??'—');
  };
  const columns=fields;
  return <section className="proc-admin proc-admin--procurement" aria-label="Procurement">
    <h2>Procurement — {superAdmin?'Central Masters':'FPO Configuration & Field Review'}</h2>
    <p>Separate procurement custody. Field receipts are provisional. Warehouse Receipt records arrival and received quantities; final grading, valuation and warehouse inventory remain deferred.</p>
    {!data&&['super_admin','fpo_admin'].includes(localStorage.getItem('role'))&&<button disabled={busy} onClick={enrol}>Enable my procurement access</button>}
    {message&&<p role="status">{message}</p>}
    {data&&<>
      <nav className="proc-admin-sections" aria-label="Procurement sections">{(superAdmin?central:['procurements',...Object.keys(forms)]).map(key=><button className={!superAdmin&&central.includes(key)?'proc-admin-section--readonly':undefined} disabled={busy} key={key} onClick={()=>{setSection(key);setValues({});setValidationAttempted(false);pending.current=null;}} aria-pressed={section===key}>{labels[key]}{!superAdmin&&central.includes(key)?' (read only)':''}</button>)}</nav>
      <h3>{labels[section]}</h3>
      {!superAdmin&&central.includes(section)&&<p className="proc-admin-readonly-note">Managed centrally by Super Admin. Available for your FPO selections.</p>}
      {fields.length>0&&data.context.role==='FPO_ADMIN'&&(superAdmin?central.includes(section):!central.includes(section))&&<form className={`proc-admin-config-form${section==='versions'?' proc-admin-rate-version-form':''}`} aria-label={section==='versions'?'Publish immutable rate version':undefined} onSubmit={submit}>
        {fields.map(([key,label,source])=>{
          if(section==='versions'&&(key==='valid_from'||key==='valid_to')){
            const preview=dateTimePreview(values[key]);
            return <label className="proc-admin-rate-date-field" key={key}><span>{label} *</span><div className="proc-admin-rate-date-control">
              <input aria-label={label} type="datetime-local" step="60" value={values[key]??''} onChange={e=>change(key,e.target.value)} required />
              {preview?<small className="proc-admin-rate-time-preview">Local time: {preview.local}<br/>Stored as UTC: {preview.utc} UTC</small>:<small className="proc-admin-rate-time-preview">Required to publish.</small>}
              {key==='valid_to'&&<small className="proc-admin-rate-exclusive-note">End time is exclusive.</small>}
            </div></label>;
          }
          return <label key={key}>{label}{source?<select aria-label={label} value={values[key]||''} onChange={e=>change(key,e.target.value)} required>
            <option value="">Select</option>{(source==='dimensions'?['MASS','COUNT','VOLUME'].map(dimension=>({id:dimension,name:dimension})):data[source]||[]).filter(row=>source!=='varieties'||!values.crop_id||row.crop_id===values.crop_id).map(row=><option key={row.id} value={row.id}>{row.name||row.code||row.id}</option>)}
          </select>:<input aria-label={label} type={key==='password'?'password':key==='decimal_places'?'number':'text'} min={key==='decimal_places'?0:undefined} max={key==='decimal_places'?6:undefined} step={key==='decimal_places'?1:undefined} autoComplete="off" value={values[key]??''} onChange={e=>change(key,e.target.value)} required />}</label>;
        })}
        {section==='versions'&&(data.grades||[]).filter(g=>g.crop_id===(data.schedules||[]).find(s=>s.id===values.schedule_id)?.crop_id).map(g=><label key={g.id}>Grade {g.name}: INR/kg <input required aria-label={`Rate ${g.name}`} value={values[`rate_${g.id}`]||''} onChange={e=>change(`rate_${g.id}`,e.target.value)} /></label>)}
        {section==='units'&&<p>Unit definitions do not convert field weights: field procurement remains kilograms to three decimals.</p>}
        {section==='versions'&&(validationAttempted||(values.valid_from&&values.valid_to))&&rateValidation&&<p className="proc-admin-rate-validation" role="alert">{rateValidation}</p>}
        <button className={section==='versions'?'proc-admin-primary-button':undefined} disabled={busy||(section==='versions'&&Boolean(rateValidation))} type="submit">{busy?'Saving…':section==='versions'?'Publish immutable version':'Create'}</button>
      </form>}
      <button className={section==='versions'?'proc-admin-secondary-button':undefined} disabled={busy} onClick={load}>Refresh</button>
      {section==='procurements'?<div className="proc-admin-review-list">{rows.length===0?<p>No field procurements.</p>:rows.map(p=>{
        const walkIn=walkInDisplay(p);
        return <details className="proc-admin-review" key={p.id}><summary>{p.id} — {p.state} — {p.net_kg} kg — INR {p.provisional_value}</summary>
          <div className="proc-admin-review-grid">
            <section className="proc-admin-review-block" aria-label="Seller & Aggregator">
              <h4>Seller &amp; Aggregator</h4>
              {walkIn?<>
                <p>Seller type: Walk-in</p>
                {walkIn.name&&<p>Name: {walkIn.name}</p>}
                {walkIn.phone&&<p>Phone: {walkIn.phone}</p>}
                {walkIn.location&&<p>Location: {walkIn.location}</p>}
                <p>Aggregator: {p.field_snapshot?.aggregator?.name||name('aggregators',p.aggregator_id)}.</p>
              </>:<p>Farmer: {p.field_snapshot?.farmer?.name||name('farmers',p.farmer_id)}; Aggregator: {p.field_snapshot?.aggregator?.name||name('aggregators',p.aggregator_id)}.</p>}
            </section>
            <section className="proc-admin-review-block" aria-label="Product & Field Grade">
              <h4>Product &amp; Field Grade</h4>
              <p>Crop: {name('crops',p.crop_id)} / {name('varieties',p.variety_id)}; field grade: {name('grades',p.grade_id)}.</p>
            </section>
            <section className="proc-admin-review-block" aria-label="Field Quantity & Provisional Value">
              <h4>Field Quantity &amp; Provisional Value</h4>
              <p>Original rate: {p.applied_rate} INR/kg. Gross: {p.gross_kg} kg. Dhalta: {p.dhalta_kg} kg.</p>
              {p.net_kg!=null&&<p>Net: {p.net_kg} kg</p>}
              {p.provisional_value!=null&&<p>Provisional value: INR {p.provisional_value}</p>}
            </section>
            <section className="proc-admin-review-block" aria-label="Lock & Server Times">
              <h4>Lock &amp; Server Times</h4>
              <p>Field lock: {p.lock_occurred_at||'Not locked'}; server received: {p.lock_received_at||'Pending'}.</p>
            </section>
            <section className="proc-admin-review-block" aria-label="Acknowledgement & Barcode">
              <h4>Acknowledgement &amp; Barcode</h4>
              <p>Acknowledgement: {p.acknowledgement_method||'Pending'} — {p.acknowledgement_reference||'Not yet recorded'}.</p>
              <p>Original rate version: {p.rate_version_id}. Barcode identifier: proc:{p.id}</p>
            </section>
            <section className="proc-admin-review-block" aria-label="Measurements">
              <h4>Measurements</h4>
              {(p.field_snapshot?.measurements||[]).map(m=><p key={m.id}>{m.type}: {m.value} {m.unit}; correction note: {m.reason}.</p>)}
            </section>
            <section className="proc-admin-review-block" aria-label="Advances">
              <h4>Physical advances — pending reconciliation</h4>
              <ul>{(data.advances||[]).filter(a=>a.procurement_id===p.id).map(a=><li key={a.id}>INR {a.amount} — {a.source} — {a.occurred_at} — {a.payment_reference}</li>)}</ul>
            </section>
            <section className="proc-admin-review-block" aria-label="Traceability / IDs">
              <h4>Traceability / IDs</h4>
              <p>Procurement ID: {p.id}</p><p>State: {p.state}</p>
            </section>
          </div>
        </details>;
      })}</div>:<div className={`proc-admin-config-table-scroll${section==='versions'?' proc-admin-rate-version-table-scroll':''}`} role="region" aria-label="Configuration table" tabIndex={0}><table className={`proc-admin-config-table${section==='versions'?' proc-admin-rate-version-table':''}`}><thead><tr>{columns.map(([key,label])=><th key={key}>{section==='versions'&&(key==='valid_from'||key==='valid_to')?label:label}</th>)}{section==='versions'&&<th>Frozen rates (INR/kg)</th>}</tr></thead><tbody>{rows.map(row=><tr key={row.id}>{columns.map(([key])=><td key={key}>{section==='versions'&&(key==='valid_from'||key==='valid_to')?(()=>{
        const value=row[key];if(!value)return '—';
        return <span className="proc-admin-rate-table-time"><span>{formatDateTime(value)}</span><small>UTC: {formatDateTime(value,'UTC')} UTC</small></span>;
      })():cell(key,row)}</td>)}{section==='versions'&&<td>{(data.rates||[]).filter(r=>r.version_id===row.id).map(r=><div key={r.id}>{name('grades',r.grade_id)}: {r.rate}</div>)}</td>}</tr>)}</tbody></table></div>}
    </>}
  </section>;
}
